import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useInstall } from '../lib/install';
import { Button, Dialog, useToast } from './ui';

/** Das Teilen-Symbol von Safari, damit man es auf dem Bildschirm wiedererkennt. */
function ShareIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="inline h-5 w-5 align-text-bottom text-brand-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12M8 7l4-4 4 4" />
      <path d="M7 10H6a2 2 0 00-2 2v7a2 2 0 002 2h12a2 2 0 002-2v-7a2 2 0 00-2-2h-1" />
    </svg>
  );
}

function AddIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="inline h-5 w-5 align-text-bottom text-slate-700" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}

/**
 * "Zum Startbildschirm hinzufügen".
 *
 * Wo der Browser es erlaubt (Chrome, Edge), oeffnet der Knopf direkt das
 * Installationsfenster. Auf dem iPhone geht das nicht - dort fuehrt er Schritt
 * fuer Schritt durch "Teilen → Zum Home-Bildschirm".
 *
 * Fuer den persoenlichen Link legt er vorher einen einmaligen Code in die
 * Adresse: die App auf dem iPhone hat einen eigenen Speicher und waere sonst
 * nach dem Ablegen nicht angemeldet.
 */
export function InstallButton({
  channel,
  variant = 'primary',
  onlyDirect = false,
}: {
  channel: 'account' | 'link';
  variant?: 'primary' | 'secondary';
  /** Nur zeigen, wenn der Browser das Installationsfenster direkt anbietet. */
  onlyDirect?: boolean;
}): JSX.Element | null {
  const { mode, install } = useInstall();
  const toast = useToast();
  const [guide, setGuide] = useState(false);
  const [busy, setBusy] = useState(false);

  if (mode === 'installed' || (onlyDirect && mode !== 'prompt')) return null;
  if (mode === 'manual') {
    return (
      <p className="hint">
        Als App ablegen: im Menü des Browsers „Zum Startbildschirm hinzufügen“ oder „App installieren“ wählen.
      </p>
    );
  }

  const open = async (): Promise<void> => {
    if (mode === 'prompt') {
      if (await install()) toast.show('Die App liegt jetzt auf dem Startbildschirm');
      return;
    }
    setBusy(true);
    try {
      if (channel === 'link') {
        const { code } = await api.post<{ code: string }>('/api/public/sports-access/transfer');
        // Diese Adresse uebernimmt Safari als Startadresse der App.
        window.history.replaceState(null, '', `/antworten?app=${code}`);
      }
      setGuide(true);
    } catch (error) {
      toast.show(error instanceof ApiError ? error.message : 'Nicht möglich', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant={variant} loading={busy} onClick={() => void open()}>
        Zum Startbildschirm hinzufügen
      </Button>
      <Dialog
        open={guide}
        onClose={() => setGuide(false)}
        title="Auf den Home-Bildschirm legen"
        footer={<Button onClick={() => setGuide(false)}>Verstanden</Button>}
      >
        <ol className="space-y-3 text-sm text-slate-700">
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800">1</span>
            <span>Unten in Safari auf <ShareIcon /> <strong>Teilen</strong> tippen.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800">2</span>
            <span>Nach unten wischen und <AddIcon /> <strong>Zum Home-Bildschirm</strong> wählen, dann <strong>Hinzufügen</strong>.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800">3</span>
            <span>
              Die App <strong>UK Info</strong> auf dem Home-Bildschirm öffnen
              {channel === 'link'
                ? ' – du bist dort gleich angemeldet – und Push einschalten.'
                : ' – du bist dort gleich angemeldet – und unter Einstellungen → Benachrichtigungen Push einschalten.'}
            </span>
          </li>
        </ol>
        <div className="mt-4 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <p className="font-medium">„Zum Home-Bildschirm“ fehlt?</p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            <li>
              In der Liste unter den Symbolen <strong>ganz nach unten</strong> wischen. Steht es auch dort nicht:
              ganz unten „Aktionen bearbeiten …“ und es hinzufügen.
            </li>
            <li>
              Aus <strong>WhatsApp, Mail oder Instagram</strong> geöffnet? Deren eingebauter Browser kann das nicht.
              Dort auf das Kompass-Symbol bzw. „In Safari öffnen“ tippen und es in Safari wiederholen.
            </li>
            <li>Im <strong>privaten Surfen</strong> fehlt die Aktion ebenfalls.</li>
          </ul>
        </div>
        <p className="hint mt-3">
          {channel === 'link'
            ? 'Bitte innerhalb eines Tages ablegen und öffnen.'
            : 'Die App gleich nach dem Ablegen öffnen – sonst ist dort einmal die Anmeldung nötig.'}
        </p>
      </Dialog>
    </>
  );
}
